import React from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../store/store";
import { Button, Dialog, DialogActions, Divider, Grid, Typography } from "@mui/material";
import Transition from "../common/Transition";
import { handleClearSingleOptimizationResults, handleToggleSingleOptimizationDialog } from "../../store/optimization-slice/optimization";
import { useTranslation } from "react-i18next";
import { Header } from "../common/Header";
import RedirectIconButton from '../common/RedirectIconButton';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

const SingleOptimizationResultDialog = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { optimizationResultDialogOpen, optimizationResult } = useAppSelector((state: RootState) => state.optimization);

    function handleClose() {
        dispatch(handleClearSingleOptimizationResults());
        dispatch(handleToggleSingleOptimizationDialog(false));
    }

    return(
        <React.Fragment>
            <Dialog
                open={optimizationResultDialogOpen}
                TransitionComponent={Transition}
                keepMounted
                onClose={(_, reason) => {
                    if(reason !== 'backdropClick') handleClose();
                }}
                aria-describedby="alert-dialog-slide-description"
                fullWidth
                maxWidth="sm"
            >
                <Header 
                    title={t('SINGLE_OPTIMIZATION_DIALOG_TITLE')} 
                    helperIcon={
                        <RedirectIconButton to={'/help/5'} className="helper-icon" >
                            <ContactSupportIcon />
                        </RedirectIconButton>
                    }
                />
                <Grid p={2}>
                    { !optimizationResult ? 
                        <Grid container>
                            <Grid item xs={12} display="flex" justifyContent="center" alignItems="center">
                                <Typography>{t('SINGLE_OPTIMIZATION_DIALOG_NO_RESULT')}</Typography>
                            </Grid>
                        </Grid> :
                        <Grid container>
                            <Grid item xs={12}>
                                <Grid container display={"flex"} justifyContent={"space-between"}>
                                    <Grid item>{t('SINGLE_OPTIMIZATION_RESULT_START_DATE')}</Grid>
                                    <Grid item>{optimizationResult?.startOptimizationDateTimeUTC ?? '---'}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container display={"flex"} justifyContent={"space-between"}>
                                    <Grid item>{t('SINGLE_OPTIMIZATION_RESULT_END_DATE')}</Grid>
                                    <Grid item>{optimizationResult?.endOptimizationDateTimeUTC ?? '---'}</Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container display={"flex"} justifyContent={"space-between"}>
                                    <Grid item>{t('SINGLE_OPTIMIZATION_RESULT_DURATION')}</Grid>
                                    <Grid item>{optimizationResult?.optimizationDuration ?? '---'}</Grid>
                                </Grid>
                            </Grid>
                            <Grid mt={2.5} item xs={12}>
                                {t('SINGLE_OPTIMIZATION_RESULTS_LABEL')}
                            </Grid>
                            <Grid mt={.5} mb={2} item xs={12}><Divider /></Grid>
                            <Grid item xs={12} sx={{ height: '400px', overflowY: 'auto' }}>
                                <Typography variant="body1" fontWeight="bold">
                                    {t(
                                        optimizationResult?.optimizationResult?.maximiseOutput
                                        ? 'OPTIMIZATION_RESULT_MAXIMIZE_HIGHEST_TITLE'
                                        : 'OPTIMIZATION_RESULT_MAXIMIZE_LOWEST_TITLE'
                                    )}
                                </Typography>
                                { optimizationResult?.optimizationResult?.bestOptions?.map((el: { outputText: string, varyingText: string[] }, index: number) => {
                                    return(
                                        <Grid
                                            key={index}
                                            item
                                            xs={12}
                                            mb={1}
                                            p={1}
                                            sx={{
                                                border: '0.5px solid gray',
                                                borderRadius: '4px',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                backgroundColor: '#f9f9f9',
                                            }}
                                        >
                                            <Grid container flexDirection="column">
                                                <Grid
                                                    item
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        flexDirection: 'row',
                                                        paddingBottom: 1,
                                                        borderBottom: '0.5px solid lightgray',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 'bold', color: '#333', marginRight: 1 }}
                                                    >
                                                        {index + 1}.
                                                    </Typography>
                                                    <Typography>{t('OPTIMIZATION_RESULT_RESULT_TEXT', { output: el.outputText })}</Typography>
                                                </Grid>
                                                <Grid item sx={{ paddingTop: 1 }}>
                                                    {el.varyingText?.map((text: string, idx: number) => (
                                                        <Typography key={idx} variant="body2" sx={{ color: '#555' }}>
                                                            {text}
                                                        </Typography>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )
                                })}
                                {
                                    optimizationResult?.optimizationResult?.otherOptions && optimizationResult?.optimizationResult?.otherOptions.length != 0 ?
                                    <Typography 
                                        fontSize="15px" 
                                        mt={1.5} 
                                        sx={{ color: '#666' }}
                                    >
                                        {t('OPTIMIZATION_RESULT_OTHER_OPTIONS')}
                                    </Typography> : '' 
                                }
                              {optimizationResult?.optimizationResult?.otherOptions?.map(
                                (el: { outputText: string; varyingText: string[] }, index: number) => {
                                    return (
                                        <Grid
                                            key={index}
                                            item
                                            xs={12}
                                            pr={1}
                                            mb={1}
                                            sx={{
                                                border: '0.5px dashed lightgray',
                                                borderRadius: '4px',
                                                backgroundColor: '#f0f0f0',
                                                padding: '8px',
                                            }}
                                        >
                                            <Grid container flexDirection="column">
                                                <Grid
                                                    item
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        flexDirection: 'row',
                                                        color: '#888',
                                                    }}
                                                >
                                                    <Typography fontSize="14px" sx={{ fontWeight: 'medium' }}>
                                                        {index + 1}. &nbsp;
                                                    </Typography>
                                                    <Typography fontSize="14px">
                                                        {t('OPTIMIZATION_RESULT_RESULT_TEXT', { output: el.outputText })}
                                                    </Typography>
                                                </Grid>
                                                <Grid item mt={1}>
                                                    {el.varyingText?.map((text: string, idx: number) => (
                                                        <Typography key={idx} fontSize="14px" sx={{ color: '#555' }}>
                                                        {text}
                                                        </Typography>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    );
                                    }
                                )}
                            </Grid>
                        </Grid> 
                    }
                </Grid>
                <DialogActions>
                    <Button size="small" variant='contained' className="button-color"  onClick={handleClose}>{t('CLOSE_BUTTON')}</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

export default SingleOptimizationResultDialog;